// Save an app-served URL as a named file download. A plain `<a download>`
// can't name cross-origin files (the API runs on :8000 while the app is on
// :3000 in dev), so fetch → blob → object-URL — the same pattern the
// exhibition export flow uses.

// Map the audio MIMEs the narration endpoints accept to friendly extensions;
// anything else falls back to the MIME subtype.
const EXT_BY_MIME: Record<string, string> = {
  "audio/mpeg": "mp3",
  "audio/mp4": "m4a",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
};

/** Download `url` and save it as `<baseName>.<ext>`, deriving the extension
 * from the response's MIME type. Throws on a non-2xx response. */
export async function downloadFile(
  url: string,
  baseName: string,
): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const ext = EXT_BY_MIME[blob.type] ?? blob.type.split("/")[1] ?? "bin";
  saveBlob(blob, `${baseName}.${ext}`);
}

/** Trigger a browser save of an in-memory blob under `filename`. Used
 * directly when the bytes came through an authenticated axios call
 * (plain `fetch` here would drop the Bearer header). */
export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
